export const chatgpt = {
  id: 'chatgpt-navigation',
  title: 'Navigating ChatGPT 3× Faster',
  subtitle: 'A research-driven redesign that makes it easy to retrace a long AI conversation',
  tags: ['AI', 'Desktop', 'UX Design', 'UX Research'],
  description: 'As ChatGPT is increasingly used for research, studying, and multi-step problem solving, longer conversations and larger chat histories place greater demands on navigation and organization.',
  role: 'End-to-end UX design',
  tools: 'Figma Design, Figma Make',
  platform: 'Web / Desktop',
  year: '2026',

  sections: [
    {
      type: 'hero-images',
      images: [
        { src: '/images/chatgpt/hero.png?v=2', alt: 'ChatGPT navigation redesign hero' }
      ]
    },
    {
      type: 'two-col-text',
      heading: 'The Problem',
      content: [
        'ChatGPT is increasingly used for sustained work — research, studying, multi-step problem solving. But the interface wasn\'t built for that. As conversations grew longer, finding a specific moment meant scrolling. As chat histories grew larger, finding the right conversation meant hunting. Most users just started over.',
        'The failure wasn\'t any single screen. It was the absence of structure at exactly the moment users needed to orient themselves.'
      ]
    },
    {
      type: 'two-col-text',
      heading: 'Research Approach',
      content: [
        'Three rounds of testing, each building on the last. Round 1 was comparative usability — participants used the native ChatGPT interface to find a prior topic in a long conversation, then locate a specific chat in a crowded sidebar. Round 2 validated whether design changes actually resolved the friction. Round 3 focused on first-use discoverability — could users find and understand new features without guidance?',
        'Because all testing was prototype-based, metrics are directional estimates drawn from observed task timing and navigation behavior.'
      ]
    },
    {
      type: 'two-col-text',
      heading: 'A Scope Pivot',
      content: [
        'Early in the project I explored visualizing branched conversations — the divergent paths created when you edit an earlier prompt. It seemed like a meaningful navigation problem.',
        'It turned out ChatGPT already handles branching at the system level. Designing around it would have meant solving cross-chat state management and navigation architecture well beyond the scope of a UI prototype. More importantly, user research showed most people didn\'t think in branches — they thought in sessions. The real problem was orientation and retrieval, not lineage.',
        'I cut the branching concept and refocused.'
      ]
    },
    {
      type: 'video',
      src: 'https://zalq7ziyrwgol5d7.public.blob.vercel-storage.com/chatgpt_scope_pivot_compressed.mp4',
      caption: "ChatGPT's existing branch feature",
    },
    {
      type: 'two-col-text',
      heading: 'Design Decisions',
      content: [
        'Four changes, all focused on adding structure without adding complexity.',
        '**Pinned Conversations** let users keep priority chats accessible regardless of recency. Immediate discoverability across all participants — the mental model mapped directly to email and messaging patterns they already knew.',
        '**Auto-Suggested Project Organization** surfaces groupings for review and approval rather than organizing silently in the background. Earlier iterations that auto-organized without asking triggered trust concerns. Giving users explicit control resolved that entirely.',
        '**Temporal Labels** add timestamps at the start of chats and key moments. Small visual cost, meaningful orientation gain for anyone returning to a long or resumed conversation.',
        'The **Chat Outline** is a collapsible outline that lives inside the chat, segmenting the conversation into labeled topics. Once discovered, every participant used it — including in shorter conversations where it wasn\'t strictly necessary.'
      ]
    },
    {
      type: 'video',
      src: 'https://zalq7ziyrwgol5d7.public.blob.vercel-storage.com/chatgpt_prototype_demo_compressed.mp4',
      caption: 'Prototype demo',
    },
    {
      type: 'image-single',
      image: { src: '/images/chatgpt/solutions.png', alt: 'Overview of all four features', caption: 'Overview of all four features' }
    },
    {
      type: 'metric-table',
      heading: 'Results',
      columns: ['Metric', 'Before', 'After'],
      columnWidths: ['2fr', '1fr', '1fr'],
      rows: [
        ['Avg. time to locate topic shift', '~45–60s', '~15–20s'],
        ['Primary reliance on scrolling', '~90%', '<20%'],
        ['Structural comprehension at a glance', '0%', '~100%'],
        ['Feature reuse within session', 'N/A', '~85–100%'],
      ],
      content: [
        'Every participant who discovered the Chat Outline reused it within the same session. The shift from ~90% scroll reliance to under 20% was the clearest signal that the structural approach worked.'
      ]
    },
    {
      type: 'callout',
      text: '"I didn\'t realize how much I was just scrolling hoping to land in the right place."'
    },
    {
      type: 'two-col-text',
      heading: 'Reflection',
      content: [
        'The features that worked best were the ones that fit into existing behavior rather than asking users to adopt new habits. Pinning worked immediately because it maps to email. The Chat Outline worked because it looks like a document outline. Auto-suggestions worked once users felt in control.',
        'The harder lesson: scope discipline matters as much as design quality. Cutting the branching concept early freed up the entire project to go deeper on what users actually needed.'
      ]
    },
    {
      type: 'image-single',
      image: { src: '/images/chatgpt/home.png', alt: 'Home with pinned chat', caption: 'Home w/pinned chat' }
    },
    {
      type: 'image-single',
      image: { src: '/images/chatgpt/auto-suggestions.png', alt: 'Project screen with auto-suggestions', caption: 'Project screen w/auto-suggestions' }
    },
    {
      type: 'image-single',
      image: { src: '/images/chatgpt/chat-options.png', alt: 'Updated chat options overlay', caption: 'Updated chat options overlay' }
    },
    {
      type: 'image-single',
      image: { src: '/images/chatgpt/chat-outline-01.png', alt: 'Chat outline idle', caption: 'Chat outline (idle)' }
    },
    {
      type: 'image-single',
      image: { src: '/images/chatgpt/chat-outline-02.png', alt: 'Chat outline active', caption: 'Chat outline (active)' }
    }
  ]
};
